import { useState } from "react";
import { Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "@/lib/site";
import { useCart } from "@/context/CartContext";
import SearchOverlay from "@/components/SearchOverlay";

const LINKS = [
  ["Correlophus ciliatus", "#morphs", true],
  ["Butik", "#shop", true],
  ["Terrarier", "#terrariums", true],
  ["Linjeavl", "/linjeavl", false, "green"],
  ["Plejeguide", "/plejeguide", false],
  ["Messekalender", "/messekalender", false],
  ["Kontakt", "/kontakt", false],
];

const FlagDK = ({ active }) => (
  <svg
    viewBox="0 0 20 15"
    className={`h-3.5 w-[18px] rounded-[2px] transition-opacity ${active ? "opacity-100" : "opacity-40"}`}
    aria-hidden="true"
  >
    <rect width="20" height="15" fill="#C8102E" />
    <rect x="6" width="3" height="15" fill="#F4F1EB" />
    <rect y="6" width="20" height="3" fill="#F4F1EB" />
  </svg>
);

const FlagEN = ({ active }) => (
  <svg
    viewBox="0 0 20 15"
    className={`h-3.5 w-[18px] rounded-[2px] transition-opacity ${active ? "opacity-100" : "opacity-40"}`}
    aria-hidden="true"
  >
    <rect width="20" height="15" fill="#012169" />
    <path d="M0 0 L20 15 M20 0 L0 15" stroke="#F4F1EB" strokeWidth="3" />
    <path d="M0 0 L20 15 M20 0 L0 15" stroke="#C8102E" strokeWidth="1.2" />
    <rect x="8" width="4" height="15" fill="#F4F1EB" />
    <rect y="5.5" width="20" height="4" fill="#F4F1EB" />
    <rect x="9" width="2" height="15" fill="#C8102E" />
    <rect y="6.5" width="20" height="2" fill="#C8102E" />
  </svg>
);

export default function Nav() {
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [lang, setLang] = useState(() =>
    typeof document !== "undefined" &&
    (document.cookie.includes("googtrans=/da/en") ||
      document.cookie.includes("googtrans=%2Fda%2Fen"))
      ? "en"
      : "da"
  );

  // Aktivér GTranslate-widget'ens egen kontrol — at sætte cookien alene oversætter ikke siden
  const setLanguage = async (next) => {
    if (next === lang) return;
    if (next === "en") toast.info("Oversætter siden til engelsk …");
    const findControl = () =>
      document.querySelector(`.gtranslate_wrapper a[data-gt-lang="${next}"]`) ||
      document.querySelector(".gtranslate_wrapper select");
    let control = findControl();
    if (!control) {
      control = await new Promise((resolve) => {
        const obs = new MutationObserver(() => {
          const c = findControl();
          if (c) {
            obs.disconnect();
            resolve(c);
          }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          obs.disconnect();
          resolve(null);
        }, 12000);
      });
    }
    if (!control) {
      toast.error("Oversættelsen kunne ikke startes — genindlæs siden og prøv igen.");
      return;
    }
    if (control.tagName === "SELECT") {
      const opt = [...control.options].find(
        (o) => o.value === next || o.value.endsWith(`|${next}`)
      );
      if (opt) {
        control.value = opt.value;
        control.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else {
      control.click();
    }
    setLang(next);
  };

  const langButtons = (
    <div className="notranslate flex items-center gap-2" translate="no">
      <button
        data-testid="lang-toggle-da"
        onClick={() => setLanguage("da")}
        title="Dansk"
        className={`transition-transform hover:scale-110 ${lang === "da" ? "rounded-sm ring-1 ring-pale" : ""}`}
      >
        <FlagDK active={lang === "da"} />
      </button>
      <button
        data-testid="lang-toggle-en"
        onClick={() => setLanguage("en")}
        title="English"
        className={`transition-transform hover:scale-110 ${lang === "en" ? "rounded-sm ring-1 ring-pale" : ""}`}
      >
        <FlagEN active={lang === "en"} />
      </button>
    </div>
  );

  const go = (target) => {
    setMenuOpen(false);
    if (target.startsWith("/")) {
      navigate(target);
      window.__lenis?.scrollTo(0, { immediate: true });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(target), 450);
    } else {
      scrollToSection(target);
    }
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/60 backdrop-blur-xl"
      >
        <div className="flex items-center justify-between px-6 py-6 lg:px-20">
          <button
            data-testid="nav-logo"
            onClick={() => {
              if (location.pathname !== "/") navigate("/");
              window.__lenis?.scrollTo(0, { duration: 1.4 });
              setMenuOpen(false);
            }}
            className="flex items-center gap-4"
          >
            <img
              src="/assets/logo-icon.png"
              alt="GEKKOFUEL logo"
              className="h-12 w-12 object-contain"
            />
            <span
              className="notranslate hidden font-sans text-sm font-bold tracking-[0.25em] text-cream 2xl:inline"
              translate="no"
            >
              GEKKOFUEL
            </span>
          </button>
          <nav className="hidden items-center gap-6 xl:flex">
            {LINKS.map(([label, target, primary, accent]) => (
              <button
                key={target}
                data-testid={`nav-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => go(target)}
                className={`group relative font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  accent === "green"
                    ? "font-bold text-[#7BA05B] hover:text-[#9BC27A]"
                    : primary
                      ? "font-bold text-cream hover:text-pale"
                      : "text-sage hover:text-cream"
                }`}
              >
                {label}
                <span className={`absolute -bottom-1 left-0 h-px w-0 transition-[width] duration-300 group-hover:w-full ${accent === "green" ? "bg-[#9BC27A]" : primary ? "bg-pale" : "bg-forest"}`} />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {langButtons}
            <button
              data-testid="nav-search-button"
              onClick={() => {
                setMenuOpen(false);
                setSearchOpen(true);
              }}
              aria-label="Søg"
              className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-pale hover:text-pale"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Søg</span>
            </button>
            <button
              data-testid="nav-account-button"
              onClick={() => {
                setMenuOpen(false);
                navigate("/min-konto");
              }}
              className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-pale hover:text-pale"
            >
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Konto</span>
            </button>
            <button
              data-testid="nav-cart-button"
              onClick={() => {
                setMenuOpen(false);
                setOpen(true);
              }}
              className="relative flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-pale hover:text-pale"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden sm:inline">Kurv</span>
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-forest font-mono text-[10px] text-cream"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="nav-enquire-button"
              onClick={() => go("#enquiry")}
              className="hidden rounded-full bg-forest px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink lg:block"
            >
              Forespørgsel
            </button>
            <button
              data-testid="nav-menu-button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center rounded-full border border-white/20 p-2.5 text-cream transition-colors hover:border-pale xl:hidden"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-[45] bg-ink/95 backdrop-blur-xl xl:hidden"
        >
          <div className="flex h-full flex-col justify-center gap-7 px-10 pt-20">
            {LINKS.map(([label, target, primary, accent], i) => (
              <button
                key={target}
                data-testid={`mobile-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => go(target)}
                className="flex items-baseline gap-4 text-left"
              >
                <span className="font-mono text-xs text-forest">0{i + 1}</span>
                <span
                  className={`font-serif text-3xl italic tracking-tight ${
                    accent === "green" ? "text-[#9BC27A]" : "text-cream"
                  }`}
                >
                  {label}
                </span>
              </button>
            ))}
            <div
              data-testid="mobile-lang-toggle"
              className="mt-4 flex items-center gap-4"
            >
              <span className="font-mono text-xs text-forest">◆</span>
              {langButtons}
            </div>
          </div>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
